// The same header on every screen: where you are, what this screen is FOR,
// and the one thing you most likely came to do.
//
// The title and the purpose line default to the menu entry for the current
// route (constants/navigation.js), so a screen can never describe itself
// differently from the menu that led here.

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FiChevronRight } from 'react-icons/fi';
import { ROUTES } from '../../constants/app.constants';
import { navItemFor } from '../../constants/navigation';

const PageHeader = ({
  title,
  purpose,
  icon: IconOverride,
  breadcrumb,          // [{ label, to }] — extra crumbs after the section
  children,            // primary action(s), right-aligned
  meta,                // small line under the title (counts, "as of…")
}) => {
  const location = useLocation();
  const nav = navItemFor(location.pathname);
  const Icon = IconOverride || nav?.icon;
  const heading = title || nav?.label || '';
  const line = purpose !== undefined ? purpose : nav?.purpose;

  return (
    <div className="mb-5">
      {/* Trail — Home › Section › This screen */}
      <nav className="flex items-center gap-1.5 text-xs text-slate-400 mb-2 flex-wrap">
        <Link to={ROUTES.TODAY} className="hover:text-slate-600 transition-colors">Today</Link>
        {nav?.section && (
          <>
            <FiChevronRight className="w-3 h-3" />
            <span className="uppercase tracking-wide">{nav.section}</span>
          </>
        )}
        <FiChevronRight className="w-3 h-3" />
        <span className="text-slate-600 font-semibold">{heading}</span>
        {(breadcrumb || []).map((c) => (
          <React.Fragment key={c.label}>
            <FiChevronRight className="w-3 h-3" />
            {c.to
              ? <Link to={c.to} className="hover:text-slate-600 transition-colors">{c.label}</Link>
              : <span className="text-slate-600 font-semibold">{c.label}</span>}
          </React.Fragment>
        ))}
      </nav>

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          {Icon && (
            <div className="w-10 h-10 rounded-xl bg-white border border-slate-200 shadow-sm flex items-center justify-center shrink-0">
              <Icon className="w-5 h-5 text-violet-600" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-black text-slate-900 leading-tight">{heading}</h1>
            {line && <p className="text-sm text-slate-500 mt-0.5">{line}</p>}
            {meta && <p className="text-xs text-slate-400 mt-1">{meta}</p>}
          </div>
        </div>
        {children && <div className="flex items-center gap-2 shrink-0">{children}</div>}
      </div>
    </div>
  );
};

export default PageHeader;
